import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

// Ag-Grid
import {
  GridOptions,
  GridApi,
  GridReadyEvent,
  DomLayoutType,
  RowSelectionOptions,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
import { AgGridAngular } from 'ag-grid-angular';
// Third Party
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgStyle } from '@angular/common';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { firstValueFrom, Subscription } from 'rxjs';
import { format } from 'date-fns';
// Services
import { SharedService } from 'src/app/shared/services/shared.service';
import { EmailService } from 'src/app/services/email.service';
// Models
import { SalesItemsColumnDef } from './columnDefs'; // models
// components
import { DeleteBtnComponent } from 'src/app/shared/components/ag-grid/delete-btn/delete-btn.component';
import { ProductServiceAutocompleteComponent } from 'src/app/shared/components/ag-grid/product-service-autocomplete/product-service-autocomplete.component';
import { SalesReceiptPdfGenerator } from '../sales-receipt-print/create-sales-receipt-pdf';
import {
  SalesItem,
  Customer,
  DepositAccounts,
  PaymentMethods,
  ProductServiceItem,
  SalesReceipt,
  salesTax,
  FormStatus,
} from 'src/app/shared/models/accounting';
import {
  Answers,
  ContactTypes,
  DateFormats,
  FormActions,
  ModalID,
  ModalType,
  Questions,
  Statements,
  StatusModalIndicator,
} from 'src/app/shared/models/constants';
import {
  availableFirearmsForDispo,
  capitalizeFirst,
  capitalizeWordsLongerThan1,
  createMd5Hash,
  formatPhoneNumber,
  removeBlankSalesRows,
} from 'src/app/shared/functions';
import { AccountingService } from '../services/accounting.service';
import {
  findInventoryItemById,
  findInventoryItemByName,
  findInventoryItemByUpc,
  getCustomerById,
  getProductServiceType,
  isProduct,
} from '../shared/functions';

@Component({
  selector: 'app-sales',
  templateUrl: './sales.component.html',
  styleUrls: ['./sales.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, BsDatepickerModule, AgGridAngular, NgStyle, NgxDropzoneModule, BsDropdownModule],
})
export class SalesComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private pdfGenerator = new SalesReceiptPdfGenerator();
  subscriptions = new Subscription();
  original_form_checksum: string;
  selected_customer: Customer = new Customer();
  sale_receipt: SalesReceipt = new SalesReceipt();
  sale_type: string = 'Sales Receipt';
  sale_action: string = FormActions.create;
  sale_id: string = '';
  sales_receipt_status: string = '';
  original_receipt_items_qty: { product_service: string; qty: number }[] = [];
  salesForm: FormGroup;
  customers: Customer[] = [];
  payment_methods = PaymentMethods;
  deposit_accounts = DepositAccounts;
  saleItemData: SalesItem[] = [];
  total: number = 0;
  taxable_subtotal: number = 0;
  subtotal: number = 0;
  sales_tax_total: string = '';
  sales_tax_options = this.accountingService.sales_tax_options;
  /** UI-only hint shown in the header badge before the receipt is saved.
   * The authoritative number is generated fresh inside save(), so this
   * preview may differ from what's ultimately persisted if another user
   * creates a receipt in the meantime. */
  preview_receipt_number: string = '';
  // placeholders
  customer_placeholder: string = 'Select Customer';
  payment_method_placeholder: string = 'Select Payment Method';

  // items_db = this.accountingService.products_services;
  isDropup = true;
  attachments: any[] = [];
  // ag-grid properties Grid
  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    enableSelectionWithoutKeys: true,
    checkboxes: false,
  };
  public formatPhoneNumber = formatPhoneNumber;
  domLayout: DomLayoutType = 'autoHeight';
  components: any;
  gridApi!: GridApi;

  columnDefs = [...SalesItemsColumnDef];
  defaultColDef;
  cellRendererParams: any;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,};
  theme = agGridDarkTheme;

  bsConfig: Partial<BsDatepickerConfig>;
  constructor(
    private formBuilder: FormBuilder,
    public sharedService: SharedService,
    private emailService: EmailService,
    public accountingService: AccountingService,
  ) {
    this.salesForm = this.formBuilder.group({
      customer: ['', Validators.required],
      email: [''],
      billing_address: [''],
      receipt_date: [
        format(new Date(), DateFormats.M_D_Y),
        Validators.required,
      ],
      sales_rep: ['', Validators.required],
      payment_method: ['', Validators.required],
      reference_number: [''],
      message_on_receipt: ['', [Validators.maxLength(255)]],
      message_on_statement: ['', [Validators.maxLength(255)]],
      sales_tax_option: ['', Validators.required],
    });
    this.gridOptions = {
      getRowHeight: function (params) {
        return 30; // Set the height you want here
      },
      // Render popup cell editors (e.g. the product/service autocomplete) on
      // document.body so they aren't clipped by overflow:hidden on grid
      // wrappers or the surrounding card.
      popupParent: typeof document !== 'undefined' ? document.body : undefined,
    };
    this.gridOptions.context = { SalesComponent: this };
    this.components = {
      deleteBtn: DeleteBtnComponent,
      productServiceAutocomplete: ProductServiceAutocompleteComponent,
    };
    this.bsConfig = Object.assign(
      {},
      {
        containerClass: 'theme-dark-blue',
        dateInputFormat: 'MM/DD/YYYY',
        showWeekNumbers: false,
        isAnimated: true,
        adaptivePosition: true,
      },
    );
    // view_edit_item$
    this.subscriptions.add(
      this.sharedService.view_edit_item$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.sale_receipt = response as SalesReceipt;
          this.populateForm(response);
          for (const item of this.saleItemData) {
            this.original_receipt_items_qty.push({
              product_service: item.product_service,
              qty: item.qty,
            });
          }
          this.updateSalesReceipt();
          this.syncOriginalChecksum();
        }),
    );
    // yes_no_submit$
    this.subscriptions.add(
      this.sharedService.yes_no_submit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (
            response['answer'] === Answers.yes &&
            response['question'].includes(Questions.void_sales_receipt)
          ) {
            this.sale_receipt.status = FormStatus.void;
            this.voidReceipt();
          } else if (
            response['answer'] === Answers.yes &&
            response['question'].includes(Questions.delete_sales_receipt)
          ) {
            this.sale_receipt.status = FormStatus.deleted;
            this.deleteReceipt();
          }
        }),
    );
    // customer_contact_to_add$
    this.subscriptions.add(
      this.accountingService.customer_contact_to_add$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.accountingService.addNewCustomer(response);
          this.sharedService.raiseSuccessToast('Customer added successfully');
        }),
    );
    // customer added
    this.subscriptions.add(
      this.accountingService.customer_added$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          console.warn('Customer added', response); // verify if a new user was added if the list is sorted
          this.applyNewCustomer(response);
        }),
    );
    // customer_list_updated
    this.subscriptions.add(
      this.accountingService.customer_list_updated$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.addCombinedName();
        }),
    );
    // option_selected
    this.subscriptions.add(
      this.sharedService.option_selected$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (response.answer === 'Inventory') {
            this.createNewInventoryItem(
              response.optional_details['item_name'],
              response.optional_details['rowIndex'],
            );
          } else {
            this.createNewService(
              response.optional_details['item_name'],
              response.optional_details['rowIndex'],
            );
          }
        }),
    );
    // product_service_to_add$
    this.subscriptions.add(
      this.accountingService.product_service_to_add$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          this.accountingService.addNewProductService(response.item);
          this.addItem(response.rowIndex, response.item);
        }),
    );
    // cancelChoice$
    this.subscriptions.add(
      this.accountingService.cancelChoice$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response) => {
          if (
            response.options.includes('Inventory') &&
            response.options.includes('Service')
          ) {
            this.saleItemData[response.optional_details['rowIndex']][
              'product_service'
            ] = null;
            this.saleItemData = [...this.saleItemData];
          }
        }),
    );
  }

  // initialize functions
  ngOnInit(): void {
    this.getCustomers();
    this.getProductsServices();
    this.addButtonColumn();
    this.addInitialGridRows();
    this.calculateTotals();
    this.addCombinedName();
    this.sharedService.available_firearms_for_dispo = availableFirearmsForDispo(
      this.sharedService.bound_book,
    );
    this.patchSalesRep();
    this.patchReceiptMessage();
    // Show a preview of the next number in the header right away (create only).
    // The number is fetched fresh at save time, so this is just a visual hint.
    if (this.sale_action === FormActions.create) {
      this.previewSalesReceiptNumber();
    }
    // this.addProductServiceValues();  // available with ag-grid enterprise

    // logged_in_user is populated from company info, which can arrive after
    // ngOnInit. Re-run the sales-rep patch when it does so the field isn't
    // left blank for users who hit the form before company data finishes loading.
    this.sharedService.company_info_retrieved$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.patchSalesRep();
        this.patchReceiptMessage();
      });

    // Keep the header validation alert in sync as the user fills the form.
    // No-op until the user has clicked Save at least once (see refreshValidationMessage).
    this.salesForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshValidationMessage());
  }
  patchReceiptMessage() {
    this.salesForm.patchValue({
      message_on_receipt: this.sharedService.company_info?.accounting_options[
        'default_sales_receipt_message'
      ],
      message_on_statement: this.sharedService.company_info?.accounting_options[
        'default_message_on_sales_statement'
      ],
    });
  }
  patchSalesRep() {
    const user = this.sharedService.logged_in_user;
    // Skip the patch when user info hasn't loaded yet — otherwise we end up
    // writing the literal string "undefined undefined" into the form because
    // string-concatenating undefined coerces to "undefined".
    if (!user?.name_first || !user?.name_last) return;
    const first_name = capitalizeFirst(user.name_first);
    const last_name = capitalizeFirst(user.name_last);
    this.salesForm.patchValue({ sales_rep: first_name + ' ' + last_name });
  }
  async getCustomers() {
    if (this.accountingService.customers.length === 0) {
      await this.accountingService.getCustomers();
    }
  }
  async getProductsServices() {
    if (this.accountingService.products_services.length === 0) {
      await this.accountingService.getProductsServices();
    }
  }
  async createSalesReceiptNumber() {
    // Ask the server for the current max — this is authoritative, so two
    // users opening the form simultaneously will at least see the same
    // baseline (the actual write order at the DB level still decides the
    // truth). Service falls back to the local list when offline.
    const maxReceiptNumber = await this.accountingService.getMaxSalesReceiptNumber();
    this.sale_receipt.sales_receipt_number = this.formatReceiptNumber(maxReceiptNumber + 1);
  }

  /** Non-binding preview of the next receipt number, shown in the header
   * badge while the user fills out the form. The actual saved number is
   * regenerated at save time via createSalesReceiptNumber(). */
  async previewSalesReceiptNumber() {
    const maxReceiptNumber = await this.accountingService.getMaxSalesReceiptNumber();
    this.preview_receipt_number = this.formatReceiptNumber(maxReceiptNumber + 1);
  }

  /** Format an integer as SRNNN (zero-padded to 3 digits, longer numbers
   * render without leading zeros). */
  private formatReceiptNumber(n: number): string {
    return 'SR' + String(n).padStart(3, '0');
  }
  addCombinedName() {
    for (const customer of this.accountingService.customers) {
      customer.contact['combined_name'] =
        `${customer.contact.name_first} ${customer.contact.name_last}`;
    }
  }
  addButtonColumn() {
    const hasEditDelete = this.columnDefs.some(
      (column) => column.headerName === 'Delete',
    );
    if (!hasEditDelete) {
      const self = this;
      this.columnDefs.push({
        // Delete
        headerName: 'Delete',
        field: 'delete',
        resizable: true,
        width: 100,
        minWidth: 100,
        pinned: 'right', // pin to right
        // The form always needs at least one line item.
        cellRendererSelector: () => {
          if (self.saleItemData.length <= 1) {
            return {
              component: 'deleteBtn',
              params: {
                disabled: true,
                tooltip: 'Add another line before deleting this one. A sales receipt must have at least one line item.',
              },
            };
          }
          return {
            component: 'deleteBtn',
            params: {
              deleteItemClick: self.deleteSalesItemClick.bind(self),
              tooltip: 'Delete Item',
            },
          };
        },
      });
    }
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  /** Re-evaluate the Delete column's cellRendererSelector for every row.
   * Called whenever the row count crosses the 1-row boundary so the button
   * appears or disappears immediately on existing rows (newly created rows
   * already get a fresh selector run). */
  private refreshDeleteColumn(): void {
    this.gridApi?.refreshCells({ columns: ['delete'], force: true });
  }
  addProductServiceValues() {
    // available with ag-grid enterprise
    for (const col of this.columnDefs) {
      if (col.field === 'product_service') {
        col['cellEditorParams'] = {
          values: this.accountingService.products_services.map(
            (product) => product.name,
          ),
        };
      }
    }
  }
  addInitialGridRows() {
    this.AddSalesItemLineClick();
    this.AddSalesItemLineClick();
  }
  addCreditCardProcessingFee() {
    const rate = this.sharedService.company_info?.accounting_options?.credit_card_processing_rate || 0;
    if (rate > 0) {
      const feeAmount = +(this.total * (rate / 100)).toFixed(2);
      
      let blankIndex = this.saleItemData.findIndex(item => !item.product_service);
      if (blankIndex === -1) {
        this.AddSalesItemLineClick();
        blankIndex = this.saleItemData.length - 1;
      }
      
      this.saleItemData[blankIndex].product_service = 'Credit Card Processing';
      this.saleItemData[blankIndex].description = 'Credit Card Processing';
      this.saleItemData[blankIndex].qty = 1;
      this.saleItemData[blankIndex].rate = feeAmount;
      this.saleItemData[blankIndex].amount = feeAmount;
      this.saleItemData[blankIndex].tax = false;
      
      this.saleItemData = [...this.saleItemData];
      this.calculateTotals();
    }
  }
  hasCreditCardFee(): boolean {
    return this.saleItemData.some((item) => item.product_service === 'Credit Card Processing');
  }
  async populateForm(form_details: SalesReceipt) {
    new Promise((resolve, reject) => {
      this.sale_action = FormActions.edit;
      this.sale_type = form_details.type;
      this.sale_id = form_details.id;
      this.salesForm.patchValue({
        customer: form_details.customer_id,
        email: form_details.email,
        billing_address: form_details.billing_address,
        receipt_date: format(form_details.receipt_date, DateFormats.M_D_Y),
        sales_rep: form_details.sales_rep,
        payment_method: form_details.payment_method,
        reference_number: form_details.reference_number,
        message_on_receipt: form_details.message_on_receipt,
        message_on_statement: form_details.message_on_statement,
      });
      this.getCustomerAddress();
      this.getCustomerInfo();
      if (form_details.sales_items.length !== 0) {
        this.saleItemData = [];
        for (const item of form_details.sales_items) {
          this.saleItemData.push(item);
        }
        this.saleItemData = [...this.saleItemData];
        this.total = form_details.total;
        this.taxable_subtotal = form_details.taxable_subtotal;
        this.subtotal = form_details.subtotal;
        // this.sales_tax_total = form_details.sales_tax_rate;
        //  this.calculateTotals();
      }
      this.customerDynamicPlaceholder();
      this.paymentMethodDynamicPlaceholder();
      resolve(true);
    });
  }
  // Item Grid Functions
  AddSalesItemLineClick() {
    const new_sales_item: SalesItem = new SalesItem();
    new_sales_item.service_date = null;
    new_sales_item.product_service = null;
    new_sales_item.description = null;
    new_sales_item.qty = null;
    new_sales_item.rate = null;
    new_sales_item.amount = null;
    new_sales_item.tax = null;
    this.saleItemData.push(new_sales_item);
    this.saleItemData = [...this.saleItemData.sort()];
    this.refreshDeleteColumn();
  }
  clearAllSaleItemsLinesClick() {
    this.saleItemData = [
      {
        service_date: null,
        product_service: null,
        description: null,
        firearm: false,
        serial_number: null,
        qty: null,
        rate: null,
        amount: null,
        tax: null,
      },
      {
        service_date: null,
        product_service: null,
        description: null,
        firearm: false,
        serial_number: null,
        qty: null,
        rate: null,
        amount: null,
        tax: null,
      },
    ];
    this.calculateTotals();
  }
  onCellValueChanged(event: any): void {
    const rowNode = event.node;
    const rowIndex = event.rowIndex;
    let new_item = new ProductServiceItem();
    new_item = findInventoryItemByUpc(
      event.data['product_service'],
      this.accountingService,
    );
    if (!new_item) {
      new_item = findInventoryItemByName(
        event.data['product_service'],
        this.accountingService,
      );
    }
    if (event.colDef.field === 'product_service') {
      if (new_item === undefined && event.newValue === '') {
        this.saleItemData[rowIndex]['description'] = null;
        this.saleItemData[rowIndex]['qty'] = null;
        this.saleItemData[rowIndex]['tax'] = null;
        this.saleItemData[rowIndex]['rate'] = null;
        this.saleItemData[rowIndex]['amount'] = null;
        this.saleItemData = [...this.saleItemData];
      } else if (new_item) {
        this.saleItemData[rowIndex]['product_service'] = new_item['upc']
          ? new_item['upc']
          : new_item['name'];
        this.saleItemData[rowIndex]['description'] = new_item['description'];
        this.saleItemData[rowIndex]['rate'] = new_item['sale_price_rate'];
        this.saleItemData[rowIndex]['qty'] = event.data['qty']
          ? event.data['qty']
          : 1;
        this.saleItemData[rowIndex]['tax'] = new_item['sales_tax'];
        this.saleItemData[rowIndex]['amount'] =
          event.data['qty'] * new_item['sale_price_rate'];
        this.saleItemData = [...this.saleItemData];
        const enough_inventory = this.checkInventoryLevels(new_item.id);
        if (!enough_inventory) {
          rowNode.setDataValue('qty_available', false);
        } else {
          rowNode.setDataValue('qty_available', true);
        }
        if (new_item.serial_item === true) {
          const item_count = event.data['qty'];
          //   this.sharedService.openSerialNumberModal(item_count, this.saleItemData[rowIndex], rowIndex);
        }
      } else {
        this.saleItemData[rowIndex]['description'] = null;
        this.saleItemData[rowIndex]['qty'] = null;
        this.saleItemData[rowIndex]['tax'] = null;
        this.saleItemData[rowIndex]['rate'] = null;
        this.saleItemData[rowIndex]['amount'] = null;
        this.saleItemData = [...this.saleItemData];
        const optional_details = {
          item_name: event.data.product_service,
          rowIndex: rowIndex,
        };
        this.sharedService.openChoiceModal(
          Questions.adding_inv_or_service,
          optional_details,
          ['Inventory', 'Service'],
        );
      }
    } else if (
      (event.colDef.field === 'qty' || event.colDef.field === 'rate') &&
      event.newValue
    ) {
      this.saleItemData[rowIndex]['qty'] = event.data['qty'];
      this.saleItemData[rowIndex]['rate'] = event.data['rate'];
      this.saleItemData[rowIndex]['amount'] =
        event.data['qty'] * event.data['rate'];
      this.saleItemData = [...this.saleItemData];

      const enough_inventory = this.checkInventoryLevels(
        new_item.id,
        event.data['qty'],
      );
      if (!enough_inventory) {
        // if not enough inventory then highlighting the qty field
        rowNode.setDataValue('qty_available', false);
      } else {
        rowNode.setDataValue('qty_available', true);
      }
    }
    this.calculateTotals();
    this.refreshValidationMessage();
  }
  addItem(rowIndex: number, new_item: ProductServiceItem) {
    this.saleItemData[rowIndex]['product_service'] = new_item['upc']
      ? new_item['upc']
      : new_item['name'];
    this.saleItemData[rowIndex]['description'] = new_item['description'];
    this.saleItemData[rowIndex]['rate'] = new_item['sale_price_rate'];
    this.saleItemData[rowIndex]['qty'] = 1;
    this.saleItemData[rowIndex]['tax'] = new_item['sales_tax'];
    this.saleItemData[rowIndex]['amount'] = 1 * new_item['sale_price_rate'];
    this.saleItemData = [...this.saleItemData];
  }
  checkInventoryLevels(item_id: string, sale_qty?: number): boolean {
    const item = findInventoryItemById(item_id, this.accountingService);
    if (item && item.type === 'Inventory') {
      const available = this.accountingService.qty_sums[item_id] ?? 0;
      const qty_to_check = sale_qty ?? 1;
      if (qty_to_check > available) {
        const toast_message = available <= 0
          ? 'Not enough inventory in stock. <br> 0 available'
          : 'Not enough inventory in stock. <br>  Only ' + available + ' available';
        this.sharedService.raiseWarningToast(toast_message);
        return false;
      }
    }
    return true;
  }
  // button functions
  deleteSalesItemClick(params: any) {
    this.saleItemData.splice(params.rowIndex, 1);
    this.saleItemData = [...this.saleItemData.sort()];
    this.refreshDeleteColumn();
    this.calculateTotals();
  }
  addNewCustomer() {
    this.sharedService.openAddEditContactModal(
      ModalType.add,
      ContactTypes.customer,
    );
  }
  editCustomer() {
    const customer = this.accountingService.customers.find(
      (customer) => customer.id === this.salesForm.get('customer').value,
    );
    this.sharedService.openAddEditContactModal(
      ModalType.edit,
      ContactTypes.customer,
      customer,
    );
  }
  async emailReceiptClick() {
    if (this.hasUnsavedChanges()) {
      this.sharedService.openStatusSpinner('Saving sales receipt.');
      await this.save();
      this.sharedService.closeStatusSpinner();
    }
    let sales_data: SalesReceipt = this.generateSalesData();
    const doc = this.pdfGenerator.generateReceiptPdf(
      sales_data,
      this.sharedService.company_info,
      await this.sharedService.getCompanyLogoDataUri(),
    );
    const base64 = this.pdfGenerator.emailPdfFile(doc);
    const titlePrefix =
      this.sharedService.company_info?.company_name?.trim() ||
      this.sharedService.company_info?.legal_name?.trim() ||
      '';
    const receiptTitle = titlePrefix ? `${titlePrefix} Sales Receipt` : 'Sales Receipt';
    const fileNamePrefix = titlePrefix || 'receipt';
    const emailData = {
      to: this.salesForm.controls['email'].value,
      subject: receiptTitle,
      text: 'Please see attached document for a copy of your sales receipt.',
      pdfBase64: base64,
      documentName: `${fileNamePrefix}_receipt_${sales_data.sales_receipt_number}.pdf`,
    };
    this.sharedService.openStatusSpinner('Emailing Sales Receipt');
    try {
      const response = await firstValueFrom(
        this.emailService.sendEmail(emailData),
      );
      this.sharedService.closeStatusSpinner();
      this.sharedService.raiseSuccessToast('Email sent successfully!');
    } catch (error: any) {
      this.sharedService.closeStatusSpinner();
      console.error('Error sending email:', error);
      if (error?.message && error.message.includes('PDF file not found')) {
        this.sharedService.raiseErrorToast('PDF file not found.');
      } else {
        this.sharedService.raiseErrorToast('Failed to send email.');
      }
    }
  }
  async printReceipt() {
    if (this.hasUnsavedChanges()) {
      this.sharedService.openStatusSpinner('Saving sales receipt.');
      await this.save();
      this.sharedService.closeStatusSpinner();
    }
    let sales_data: SalesReceipt = this.generateSalesData();
    this.sharedService.openSalesReceiptPrintModal(sales_data);
  }
  voidReceiptClick() {
    this.sharedService.openYesNoModal(
      `Are you sure you want to void receipt ${this.sale_receipt.sales_receipt_number}?`,
    );
  }
  async voidReceipt() {
    this.sharedService.openStatusSpinner(Statements.sales_receipt_voiding);
    this.sale_receipt.status = FormStatus.void;
    await this.accountingService.editSalesReceipt(
      this.buildSalesReceiptVoidObject(),
    );
  }
  deleteReceiptClick() {
    this.sharedService.openYesNoModal(
      `Are you sure you want to delete receipt ${this.sale_receipt.sales_receipt_number}?`,
    );
  }
  async deleteReceipt() {
    this.sharedService.openStatusSpinner(Statements.sales_receipt_deleting);
    this.accountingService.deleteSalesReceipt(this.sale_id);
  }
  /** Human-readable labels for the required form controls, keyed by control name. */
  private static readonly REQUIRED_FIELD_LABELS: Record<string, string> = {
    customer: 'Customer',
    receipt_date: 'Receipt Date',
    sales_rep: 'Sales Rep',
    payment_method: 'Payment Method',
    sales_tax_option: 'Sales Tax',
  };

  /** Click handler for the Save / Save and Close buttons. The buttons are
   * visually disabled while the form is invalid but remain clickable so we
   * can tell the user *which* required field is missing. */
  /** Friendly names of required fields that are still empty. Empty array
   * hides the alert. Rendered as chips in the header so each missing item
   * reads as a distinct token. */
  missing_fields: string[] = [];

  /** Compute the friendly names of every required field that isn't yet
   * satisfied. The grid's line-item requirement is included alongside the
   * form controls because it's part of the Save gate. */
  private computeMissingFields(): string[] {
    const missing: string[] = [];
    for (const [control, label] of Object.entries(SalesComponent.REQUIRED_FIELD_LABELS)) {
      if (this.salesForm.get(control)?.invalid) missing.push(label);
    }
    if (!this.hasProductServiceProperty()) missing.push('At least one line item');
    return missing;
  }

  /** Re-evaluate the alert in response to user edits. Only runs while the
   * alert is already visible — we don't want to silently surface it before
   * the user has clicked Save at least once. */
  refreshValidationMessage(): void {
    if (this.missing_fields.length === 0) return;
    this.missing_fields = this.computeMissingFields();
  }

  attemptSave(closeAfter: boolean): void {
    const missing = this.computeMissingFields();
    if (missing.length > 0) {
      this.salesForm.markAllAsTouched();
      this.missing_fields = missing;
      return;
    }

    this.missing_fields = [];
    if (closeAfter) {
      this.saveAndClose();
    } else {
      this.save();
    }
  }

  async save() {
    this.saleItemData = removeBlankSalesRows(this.saleItemData);
    // creating/updating the sales receipt object
    if (this.sale_id !== '') {
      this.sale_receipt.id = this.sale_id;
    }
    // Generate the receipt number now (only for new receipts) so the lookup
    // happens as close to the actual save as possible — this shrinks the
    // window where another user could create a receipt and steal the number
    // out from under us. Edit flows keep their existing sales_receipt_number.
    if (this.sale_action === FormActions.create) {
      await this.createSalesReceiptNumber();
    }
    this.updateSalesReceipt();
    if (this.sale_action === FormActions.create) {
      await this.accountingService.addNewSalesReceipt(this.sale_receipt);
      // Pick up the server-assigned id from the refreshed list so a follow-up
      // edit on the same form has a valid id to PUT against. Without this,
      // sale_action flips to 'Edit' but sale_receipt.id is still undefined,
      // causing localDb.salesReceipts.put() to throw a Dexie keyPath error
      // on the next save.
      const saved = this.accountingService.sales_records.find(
        (r) => r.sales_receipt_number === this.sale_receipt.sales_receipt_number
      );
      if (saved?.id) {
        this.sale_receipt.id = saved.id;
        this.sale_id = saved.id;
      }
      this.sale_action = 'Edit';
    } else if (this.sale_action === FormActions.edit) {
      await this.accountingService.editSalesReceipt(this.sale_receipt);
    }

    // Keep the baseline checksum aligned with the last persisted state.
    this.syncOriginalChecksum();
  }

  private hasUnsavedChanges(): boolean {
    this.updateSalesReceipt();
    return (
      this.original_form_checksum !==
      createMd5Hash(JSON.stringify(this.sale_receipt))
    );
  }

  private syncOriginalChecksum(): void {
    this.original_form_checksum = createMd5Hash(
      JSON.stringify(this.sale_receipt),
    );
  }

  async saveAndClose() {
    this.sharedService.openStatusSpinner('Saving sales receipt.');
    await this.save();
    this.sharedService.closeStatusSpinner();
    this.closeForm();
  }
  updateSalesReceipt() {
    this.sale_receipt.type = this.sale_type;
    this.sale_receipt.customer_id = this.salesForm.get('customer').value;
    this.sale_receipt.customer_name =
      this.selected_customer.contact.display_name;
    this.sale_receipt.email = this.salesForm.get('email').value;
    this.sale_receipt.billing_address = this.selected_customer.address;
    this.sale_receipt.receipt_date = this.salesForm.get('receipt_date').value;
    this.sale_receipt.sales_rep = this.salesForm.get('sales_rep').value;
    this.sale_receipt.payment_method =
      this.salesForm.get('payment_method').value;
    this.sale_receipt.reference_number =
      this.salesForm.get('reference_number').value;
    let sales_items = this.saleItemData.filter(
      (item) => item.product_service !== null,
    );
    this.sale_receipt.sales_items = sales_items;
    this.sale_receipt.message_on_receipt = this.salesForm.get(
      'message_on_receipt',
    ).value
      ? this.salesForm.get('message_on_receipt').value
      : '';
    this.sale_receipt.message_on_statement = this.salesForm.get(
      'message_on_statement',
    ).value
      ? this.salesForm.get('message_on_statement').value
      : '';
    this.sale_receipt.subtotal = this.subtotal;
    this.sale_receipt.taxable_subtotal = this.taxable_subtotal;
    this.sale_receipt.sales_tax_rate =
      this.salesForm.get('sales_tax_option').value;
    this.sale_receipt.total = +this.total.toFixed(2);
    this.sale_receipt.amount_received = +this.total.toFixed(2);
    this.sale_receipt.balance_due = 0;
    this.sale_receipt.status = FormStatus.paid;
  }
  generateSalesData(): SalesReceipt {
    let sales_data: SalesReceipt = {
      billing_address: {
        street: this.selected_customer.address.street
          ? this.selected_customer.address.street
          : '',
        street2: this.selected_customer.address.street2
          ? this.selected_customer.address.street2
          : '',
        city: this.selected_customer.address.city
          ? this.selected_customer.address.city
          : '',
        state: this.selected_customer.address.state
          ? this.selected_customer.address.state
          : '',
        zip_code: this.selected_customer.address.zip_code
          ? this.selected_customer.address.zip_code
          : '',
        country: this.selected_customer.address.country
          ? this.selected_customer.address.country
          : '',
      },
      sales_items: this.saleItemData,
      sales_receipt_number: this.sale_receipt.sales_receipt_number,
      type: 'Sales Receipt',
      customer_id: this.selected_customer.id,
      customer_name: this.selected_customer.contact.display_name,
      email: this.selected_customer.contact.email
        ? this.selected_customer.contact.email
        : '',
      receipt_date: this.salesForm.get('receipt_date').value,
      sales_rep: this.salesForm.get('sales_rep').value,
      payment_method: this.salesForm.get('payment_method').value,
      reference_number: this.salesForm.get('reference_number').value,
      message_on_receipt: this.salesForm.get('message_on_receipt').value,
      message_on_statement: this.salesForm.get('message_on_statement').value,
      subtotal: this.subtotal,
      taxable_subtotal: this.taxable_subtotal,
      sales_tax_rate: +this.selected_customer.tax_option.combined_rate,
      total: this.total,
      amount_received: this.total,
      balance_due: 0,
      status: this.sale_receipt.status,
      id: this.sale_receipt.id,
    };
    return sales_data;
  }
  closeForm() {
    this.sharedService.changeRoute('accounting-sales-table');
  }
  buildSalesReceiptVoidObject() {
    const customer: Customer = getCustomerById(
      this.salesForm.get('customer').value,
      this.accountingService,
    );
    this.sale_receipt.id = this.sale_id;
    this.sale_receipt.type = this.sale_type;
    this.sale_receipt.customer_id = customer.id;
    this.sale_receipt.customer_name = customer.contact.display_name;
    this.sale_receipt.email = this.salesForm.get('email').value;
    this.sale_receipt.billing_address = customer.address;
    this.sale_receipt.receipt_date = this.salesForm.get('receipt_date').value;
    this.sale_receipt.sales_rep = this.salesForm.get('sales_rep').value;
    this.sale_receipt.payment_method =
      this.salesForm.get('payment_method').value;
    this.sale_receipt.reference_number =
      this.salesForm.get('reference_number').value;
    let sales_items = this.saleItemData.filter(
      (item) => item.product_service !== null,
    );
    this.sale_receipt.sales_items = sales_items;
    this.sale_receipt.message_on_receipt =
      this.salesForm.get('message_on_receipt').value;
    this.sale_receipt.message_on_statement = this.salesForm.get(
      'message_on_statement',
    ).value;
    this.sale_receipt.subtotal = this.subtotal;
    this.sale_receipt.taxable_subtotal = this.taxable_subtotal;
    this.sale_receipt.sales_tax_rate =
      this.salesForm.get('sales_tax_option').value;
    this.sale_receipt.total = +this.total.toFixed(2);
    this.sale_receipt.amount_received = +this.total.toFixed(2);
    this.sale_receipt.balance_due = 0;
    this.sale_receipt.status = FormStatus.void;
    return this.sale_receipt;
  }
  // form functions
  calculateTotals() {
    this.total = 0;
    this.subtotal = 0;
    this.taxable_subtotal = 0;
    this.sales_tax_total = '';
    for (const item of this.saleItemData) {
      // calculating subtotal
      if (item['amount']) {
        this.subtotal = +(+this.subtotal + +item['amount']).toFixed(2);
      }
      // calculating taxable subtotal
      if (item['amount'] && item['tax'] === true) {
        this.taxable_subtotal = +(
          +this.taxable_subtotal + +item['amount']
        ).toFixed(2);
      }
    }
    // calculating tax
    this.sales_tax_total = (
      +this.taxable_subtotal *
      1.0 *
      +this.salesForm.get('sales_tax_option').value
    ).toFixed(2);
    // calculating total
    if (this.salesForm.get('sales_tax_option').value) {
      this.total = +(+this.subtotal + +this.sales_tax_total);
    } else {
      this.total = +this.subtotal.toFixed(2);
    }
  }
  addAttachment(event: any) {
    if (event.addedFiles.length !== 0) {
      const newFiles = event.addedFiles.filter(
        (newFile: any) => !this.attachments.some(
          (existingFile) => existingFile.name === newFile.name && existingFile.size === newFile.size
        )
      );

      if (newFiles.length === 0 && event.addedFiles.length > 0) {
        this.sharedService.raiseWarningToast('File is already attached.');
        return;
      }

      if (this.attachments.length + newFiles.length <= 2) {
        this.attachments.push(...newFiles);
      } else {
        this.sharedService.raiseWarningToast(
          'You can only attach 2 files to an sales record',
        );
      }
    } else if (event.rejectedFiles.length !== 0) {
      if (event.rejectedFiles[0]['reason'] === 'size') {
        this.sharedService.raiseWarningToast(
          'File size is too large. Please upload a file less than 20MB',
        );
      }
      if (event.rejectedFiles[0]['reason'] === 'type') {
        this.sharedService.raiseWarningToast(
          'File type is incorrect. Please upload a PDF file less than 20MB',
        );
      }
    } else {
      this.sharedService.raiseWarningToast(
        `Can not attached file due to:${event.rejectedFiles[0]['reason'].value}`,
      );
    }
  }
  removeAttachment(file: any) {
    this.attachments.splice(this.attachments.indexOf(event), 1);
  }
  getCustomerInfo() {
    if (this.salesForm.controls['customer'].value !== null) {
      this.selected_customer = getCustomerById(
        this.salesForm.controls['customer'].value,
        this.accountingService,
      );
      this.getCustomerAddress();
      this.getCustomerEmail();
      this.getCustomerTaxOption();
    }
  }
  getCustomerEmail() {
    this.salesForm.patchValue({
      email: this.selected_customer.contact.email,
    });
  }
  getCustomerAddress() {
    const c = this.selected_customer;
    let billing_address: string = '';

    const name_first = c.contact.name_first ? c.contact.name_first : '';
    const name_last = c.contact.name_last ? c.contact.name_last : '';
    const street = c.address.street ? c.address.street : '';
    const street2 = c.address.street2 ? c.address.street2 : '';
    const city = c.address.city ? c.address.city : '';
    const state = c.address.state ? c.address.state : '';
    const zip_code = c.address.zip_code ? c.address.zip_code : '';

    billing_address =
      `${capitalizeFirst(name_first)} ${capitalizeFirst(name_last)}`.trim() +
      (street ? `\n${capitalizeWordsLongerThan1(street)}` : '') +
      (street2 ? ` ${capitalizeWordsLongerThan1(street2)}` : '') +
      (capitalizeFirst(city) || state || zip_code
        ? `\n${[city, state, zip_code]
            .filter(Boolean)
            .join(', ')
            .replace(/, $/, '')}`
        : '');

    this.salesForm.patchValue({
      billing_address: billing_address.trim(),
    });
  }
  getCustomerTaxOption() {
    if (
      this.selected_customer &&
      this.selected_customer.tax_option &&
      this.selected_customer.tax_option.name
    ) {
      // The customer document only stores the tax option name — no rate fields.
      // Look up the matching option from the company's sales_tax_options list
      // (which has the full rates) and use its combined_rate, since the
      // ng-select uses bindValue="combined_rate" to match selections.
      const matchedOption = this.sales_tax_options.find(
        (opt) => opt.name === this.selected_customer.tax_option.name,
      );
      this.salesForm.patchValue({
        sales_tax_option: matchedOption
          ? matchedOption.combined_rate
          : this.selected_customer.tax_option.combined_rate,
      });
    } else {
      this.salesForm.patchValue({
        sales_tax_option: '',
      });
    }
    this.calculateTotals();
  }
  clearCustomerFields() {
    this.clearCustomerAddress();
    this.clearCustomerEmail();
  }
  clearCustomerAddress() {
    this.salesForm.patchValue({
      billing_address: '',
    });
  }
  clearCustomerEmail() {
    this.salesForm.patchValue({
      email: '',
    });
  }

  createNewService(item_name: string, rowIndex?: number) {
    const item = new ProductServiceItem();
    item.type = 'Service';
    item.name = item_name;
    this.sharedService.openCreateProductService(ModalType.add, item, rowIndex);
  }
  createNewInventoryItem(item_name: string, rowIndex?: number) {
    const item = new ProductServiceItem();
    item.type = 'Inventory';
    item.name = item_name;
    this.sharedService.openCreateProductService(ModalType.add, item, rowIndex);
  }
  hasProductServiceProperty(): boolean {
    if ('product_service' in this.saleItemData[0]) {
      if (this.saleItemData[0]['product_service']) {
        return true;
      } else {
        return null;
      }
    } else if ('product_service' in this.saleItemData[1]) {
      if (this.saleItemData[1]['product_service']) {
        return true;
      } else {
        return null;
      }
    } else {
      return false;
    }
  }

  customerDynamicPlaceholder() {
    if (
      this.salesForm.controls['customer'].value === '' ||
      this.salesForm.controls['customer'].value === null
    ) {
      this.customer_placeholder = 'Select Customer';
    } else {
      this.customer_placeholder = '';
    }
  }
  paymentMethodDynamicPlaceholder() {
    if (
      this.salesForm.controls['payment_method'].value === '' ||
      this.salesForm.controls['payment_method'].value === null
    ) {
      this.payment_method_placeholder = 'Select Payment Method';
    } else {
      this.payment_method_placeholder = '';
    }
  }
  applyNewCustomer(new_customer: Customer) {
    this.salesForm.patchValue({ customer: new_customer.id });
    this.getCustomerInfo();
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
